import { useEffect, useSyncExternalStore } from "react";
import { isTypingTarget, isModalOpen } from "@/lib/keyboard-utils";
import { isChromeFreeKeyboardScopeMounted, subscribeChromeFreeKeyboardScopeMounted } from "@/components/layout/AppLayoutMountSignal";

const SEQUENCE_TIMEOUT_MS = 1000;

export function useGlobalHotkeys(enabled = true) {
  // AuthenticatedProviders also owns a copy: all copies defer to mounted task scope.
  const taskOwnsKeyboard = useSyncExternalStore(subscribeChromeFreeKeyboardScopeMounted, isChromeFreeKeyboardScopeMounted, () => false);
  useEffect(() => {
    // Tasks own their keyboard interactions, including Escape and browser shortcuts.
    if (!enabled || taskOwnsKeyboard) return;
    let pendingSequence: "g" | null = null;
    let sequenceTimeoutId: number | null = null;

    const clearSequence = () => {
      pendingSequence = null;
      if (sequenceTimeoutId !== null) {
        window.clearTimeout(sequenceTimeoutId);
        sequenceTimeoutId = null;
      }
    };

    const startSequence = () => {
      pendingSequence = "g";
      if (sequenceTimeoutId !== null) {
        window.clearTimeout(sequenceTimeoutId);
      }
      sequenceTimeoutId = window.setTimeout(clearSequence, SEQUENCE_TIMEOUT_MS);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const rawKey = typeof event.key === "string" ? event.key : "";
      const key = rawKey.toLowerCase();
      const isModifierPressed = event.metaKey || event.ctrlKey || event.altKey;

      if ((event.metaKey || event.ctrlKey) && key === "k") {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("open-search"));
        return;
      }

      if ((event.metaKey || event.ctrlKey) && (rawKey === "?" || (rawKey === "/" && event.shiftKey))) {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("open-solomon"));
        return;
      }

      if ((event.metaKey || event.ctrlKey) && key === "n") {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("new-contribution"));
        return;
      }

      if ((event.metaKey || event.ctrlKey) && key === "p") {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("open-person-lookup"));
        return;
      }

      if ((event.metaKey || event.ctrlKey) && key === "/") {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("open-shortcuts-help"));
        return;
      }

      // Bare "/" opens search — but only as a true single-key shortcut. Skip it
      // when the user is typing (input/textarea/contenteditable) or when a modal
      // dialog is already open, so it can never hijack a slash typed into a
      // dialog field or fire behind an open overlay (UX audit Jun 10, task 4.4).
      if (rawKey === "/" && !isTypingTarget(event.target) && !isModalOpen()) {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("open-search"));
        return;
      }

      // Escape must always work (closing search / dialogs), so it is exempt from
      // the typing / modal guards below.
      if (rawKey === "Escape") {
        window.dispatchEvent(new CustomEvent("close-search"));
        clearSequence();
        return;
      }

      // Suppress remaining single-key shortcuts (the g / g-p navigation
      // sequence) while typing OR while a modal dialog is open.
      if (isTypingTarget(event.target) || isModalOpen()) {
        clearSequence();
        return;
      }

      if (!isModifierPressed && key === "g") {
        startSequence();
        return;
      }

      if (pendingSequence === "g" && !isModifierPressed && key === "p") {
        event.preventDefault();
        clearSequence();
        window.dispatchEvent(new CustomEvent("go-people"));
        return;
      }

      clearSequence();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      clearSequence();
    };
  }, [enabled, taskOwnsKeyboard]);
}
